/**
 * OperationsDashboard — Phase 25B
 * Minimal cashier-facing landing. Strictly operational:
 *   • Today's own collection (self-only — no school-wide totals)
 *   • Quick-find student search (single-line, primary action)
 *   • Recent 20 receipts collected by me
 *
 * Intentionally NOT shown here (those live in the management cockpit):
 *   • School-wide today's collection
 *   • Trend charts, alerts
 *   • Top pending classes, by-class rankings
 *   • Concession totals, session-wide expected revenue
 *
 * Same /dashboard route is used; `RoleHomeRedirect` in App.js picks this
 * renderer for cashier and (optionally) school_admin in the future.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlass,
  Receipt,
  CurrencyInr,
  ArrowRight,
  Clock,
  HandCoins,
  X as CloseIcon,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const MODE_TONE = {
  CASH: "bg-emerald-50 text-emerald-700 border-emerald-200",
  UPI: "bg-blue-50 text-blue-700 border-blue-200",
  BANK: "bg-violet-50 text-violet-700 border-violet-200",
  "—": "bg-slate-100 text-slate-600 border-slate-200",
};

export default function OperationsDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // --- Search state ---
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchDebounce = useRef(null);
  const searchAbort = useRef(null);

  const loadToday = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/school/operations/me/today");
      setData(d);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  // Debounced live search
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (searchAbort.current) searchAbort.current.abort();
    const term = q.trim();
    if (term.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearching(true);
    setSearchOpen(true);
    searchDebounce.current = setTimeout(async () => {
      try {
        searchAbort.current = new AbortController();
        const { data: r } = await api.get(
          "/school/operations/students/search",
          { params: { q: term, limit: 12 }, signal: searchAbort.current.signal }
        );
        setSearchResults(r.items || []);
      } catch (_e) {
        // ignore aborts
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
  }, [q]);

  const today = data?.today || { amount: 0, receipts: 0, mode_mix: {} };
  const recent = data?.recent_receipts || [];

  const modeRows = useMemo(() => {
    const mm = today.mode_mix || {};
    return [
      { key: "CASH", label: "Cash", value: mm.cash || 0 },
      { key: "UPI", label: "UPI", value: mm.upi || 0 },
      { key: "BANK", label: "Bank", value: mm.bank || 0 },
      { key: "OTHER", label: "Other", value: mm.other || 0 },
    ].filter((r) => r.value > 0);
  }, [today.mode_mix]);

  return (
    <div
      className="space-y-6 max-w-5xl"
      data-testid="operations-dashboard"
    >
      {/* Greeting */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5">
            <HandCoins size={12} weight="duotone" /> Operations
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Hello, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Find a student, collect fees, review your recent receipts.
          </p>
        </div>
      </div>

      {/* QUICK FIND — primary affordance, big & front-and-center */}
      <div className="relative" data-testid="quick-find-wrapper">
        <label
          className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1.5 block"
          htmlFor="quick-find-input"
        >
          Quick find — student name or admission no
        </label>
        <div className="relative">
          <MagnifyingGlass
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            id="quick-find-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Aarav  or  ADM-23-001"
            className="pl-10 pr-10 h-12 text-base"
            autoComplete="off"
            data-testid="quick-find-input"
            onFocus={() => q.trim().length >= 2 && setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 180)}
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setSearchResults([]);
                setSearchOpen(false);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid="quick-find-clear"
            >
              <CloseIcon size={16} />
            </button>
          )}
        </div>

        {searchOpen && (
          <div
            className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
            data-testid="quick-find-results"
          >
            {searching && searchResults.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500">Searching…</div>
            ) : searchResults.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-400">
                {q.trim().length < 2
                  ? "Type at least 2 characters…"
                  : "No matching student."}
              </div>
            ) : (
              <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                {searchResults.map((s) => (
                  <li key={s.id}>
                    <Link
                      to={`/students/${s.id}`}
                      className="block px-4 py-2.5 hover:bg-slate-50"
                      data-testid={`quick-find-row-${s.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {s.name}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            {s.admission_no}
                            {s.class_label ? `  ·  Class ${s.class_label}` : ""}
                          </div>
                        </div>
                        <ArrowRight
                          size={14}
                          className="text-slate-400 shrink-0"
                        />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && (
        <div
          className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md"
          data-testid="operations-error"
        >
          {error}
        </div>
      )}

      {/* TODAY (self-only) */}
      <div
        className="border border-slate-200 bg-white rounded-lg p-4 sm:p-5"
        data-testid="today-self-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              My collection today
            </div>
            <div className="text-3xl font-bold text-slate-900 font-heading mt-1 font-mono">
              {loading ? "…" : formatINR(today.amount)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {loading ? (
                "Loading…"
              ) : (
                <>
                  <strong className="text-slate-700">{today.receipts}</strong>{" "}
                  receipts ·{" "}
                  <span className="text-slate-400">
                    cash handover view (self only)
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {modeRows.length === 0 ? (
              <span className="text-xs text-slate-400">No receipts yet today.</span>
            ) : (
              modeRows.map((m) => (
                <span
                  key={m.key}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-md border font-mono ${
                    MODE_TONE[m.key] || MODE_TONE["—"]
                  }`}
                  data-testid={`mode-pill-${m.key}`}
                >
                  {m.label} {formatINR(m.value)}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* RECENT RECEIPTS (mine) */}
      <div
        className="border border-slate-200 bg-white rounded-lg"
        data-testid="recent-receipts-card"
      >
        <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5">
              <Receipt size={12} weight="duotone" /> My recent receipts
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Last {recent.length} collected by you
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-receipts-table">
            <thead className="bg-slate-50">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-3 sm:px-5 py-2">
                  Receipt
                </th>
                <th className="text-left font-semibold px-3 py-2">Student</th>
                <th className="text-left font-semibold px-3 py-2">Mode</th>
                <th className="text-left font-semibold px-3 py-2">For month</th>
                <th className="text-right font-semibold px-3 sm:px-5 py-2">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && recent.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-10 text-center text-slate-500"
                  >
                    Loading…
                  </td>
                </tr>
              ) : recent.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-10 text-center text-slate-400"
                  >
                    No receipts collected yet. Use Quick-find above to start a
                    collection.
                  </td>
                </tr>
              ) : (
                recent.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50"
                    data-testid={`recent-row-${r.id}`}
                  >
                    <td className="px-3 sm:px-5 py-2.5">
                      <div className="font-mono text-xs text-slate-900">
                        {r.receipt_no}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                        <Clock size={10} /> {formatTime(r.created_at)}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-slate-900 truncate max-w-[200px]">
                        {r.student_name}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {r.admission_no}
                        {r.class_label ? `  ·  ${r.class_label}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${
                          MODE_TONE[r.mode] || MODE_TONE["—"]
                        }`}
                      >
                        {r.mode}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">
                      {r.period_month || "—"}
                    </td>
                    <td className="px-3 sm:px-5 py-2.5 text-right font-mono font-semibold text-emerald-700">
                      {formatINR(r.amount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-slate-400 pt-1">
        <CurrencyInr size={11} className="inline -mt-0.5 mr-0.5" />
        Operations view — strictly your own activity today. School-wide
        analytics live with the principal/accountant role.
      </div>
    </div>
  );
}
